// C:\Users\purplehat\Desktop\payment-cookie-system\frontend\src\services\socket.js

import { io } from 'socket.io-client';
import toast from 'react-hot-toast';

const SOCKET_URL = process.env.REACT_APP_API_URL?.replace('/api', '') || 'https://bckend-1-fi3u.onrender.com';

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.userId = null;
    this.currentRoom = null;
  }

  connect() {
    if (this.socket) return;

    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const u = JSON.parse(userStr);
        this.userId = String(u.id || u._id);
      } catch (e) {}
    }

    this.socket = io(SOCKET_URL, {
      auth: { token },                 // 🔥 JWT en el handshake (backend nuevo)
      withCredentials: true,
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: 1500,
      reconnectionDelayMax: 5000,
      timeout: 20000
    });

    // ============================================
    // 🔌 CONECTADO
    // ============================================
    this.socket.on('connect', () => {
      console.log('🔌 Conectado al servidor de tiempo real');
      this.reconnectAttempts = 0;

      // Autenticar (para backend legacy con 'authenticate')
      if (this.userId) {
        this.socket.emit('authenticate', this.userId);
        this.socket.emit('join-user', this.userId);   // 🔔 para notificaciones
        console.log('✅ Autenticado:', this.userId);
      }

      // Re-unirse a la sala activa si había una
      if (this.currentRoom) {
        this.socket.emit('join_room', this.currentRoom);
        this.socket.emit('join_chat', this.currentRoom);
        console.log('🔄 Re-unido a sala:', this.currentRoom);
      }

      this.socket.emit('join-global');
      this.socket.emit('request-stats');
    });

    // ============================================
    // ❌ DESCONECTADO
    // ============================================
    this.socket.on('disconnect', (reason) => {
      console.log('🔌 Desconectado:', reason);
      if (reason === 'io server disconnect') {
        this.socket.connect();
      }
    });

    // ============================================
    // ⚠️ ERROR
    // ============================================
    this.socket.on('connect_error', (error) => {
      this.reconnectAttempts++;
      console.error('❌ connect_error:', error.message);
      if (this.reconnectAttempts >= this.maxReconnectAttempts) {
        toast.error('📡 Sin conexión en tiempo real', { duration: 5000 });
      }
    });

    // ============================================
    // 🔄 RECONEXIÓN
    // ============================================
    this.socket.on('reconnect', (attempt) => {
      console.log('🔄 Reconectado tras', attempt, 'intentos');
      if (this.userId) {
        this.socket.emit('authenticate', this.userId);
        this.socket.emit('join-user', this.userId);
      }
      if (this.currentRoom) {
        this.socket.emit('join_room', this.currentRoom);
        this.socket.emit('join_chat', this.currentRoom);
      }
    });

    // ============================================
    // 📡 EVENTOS DEL SISTEMA
    // ============================================
    this.socket.on('recent-activity', (d) => this.notifyListeners('recent-activity', d));
    this.socket.on('new-cookie', (d) => this.notifyListeners('new-cookie', d));
    this.socket.on('payment', (d) => this.notifyListeners('payment', d));
    this.socket.on('verification', (d) => this.notifyListeners('verification', d));
    this.socket.on('card-added', (d) => this.notifyListeners('card-added', d));
    this.socket.on('live-stats', (d) => this.notifyListeners('live-stats', d));
    this.socket.on('activity', (d) => this.notifyListeners('activity', d));
    this.socket.on('amazon-real-attempt', (d) => this.notifyListeners('amazon-real-attempt', d));
    this.socket.on('balance_updated', (d) => this.notifyListeners('balance_updated', d));

    // ============================================
    // 💬 EVENTOS DEL CHAT
    // ============================================
    this.socket.on('new_message', (data) => {
      console.log('💬 new_message:', data?._id);
      this.notifyListeners('new_message', data);
    });

    this.socket.on('new_message_notification', (data) => {
      console.log('🔔 new_message_notification:', data?.roomName);
      this.notifyListeners('new_message_notification', data);
    });

    this.socket.on('user_joined', (d) => {
      console.log('👤 user_joined:', d);
      this.notifyListeners('user_joined', d);
    });

    this.socket.on('user_left', (d) => {
      console.log('👤 user_left:', d);
      this.notifyListeners('user_left', d);
    });

    this.socket.on('user_typing', (d) => this.notifyListeners('user_typing', d));

    this.socket.on('message_read', (d) => {
      console.log('✓ message_read:', d);
      this.notifyListeners('message_read', d);
    });

    this.socket.on('room_read', (d) => this.notifyListeners('room_read', d));
    this.socket.on('room_marked_read', (d) => this.notifyListeners('room_marked_read', d));

    this.socket.on('message_deleted', (d) => {
      console.log('🗑️ message_deleted:', d);
      this.notifyListeners('message_deleted', d);
    });

    // 🔥 NUEVOS — status online y cambios en grupos
    this.socket.on('user_status', (d) => {
      console.log('🟢 user_status:', d);
      this.notifyListeners('user_status', d);
    });

    this.socket.on('group_members_updated', (d) => {
      console.log('👥 group_members_updated:', d);
      this.notifyListeners('group_members_updated', d);
    });

    // Errores de join
    this.socket.on('join_error', (d) => {
      console.warn('⚠️ join_error:', d);
      this.notifyListeners('join_error', d);
    });
  }

  // ============================================
  // API PÚBLICA
  // ============================================
  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
      this.currentRoom = null;
    }
  }

  setUserId(id) {
    this.userId = String(id);
    if (this.socket?.connected) {
      this.socket.emit('authenticate', this.userId);
      this.socket.emit('join-user', this.userId);
    }
    console.log('👤 userId seteado:', this.userId);
  }

  getUserId() {
    // Fallback: leer del localStorage si aún no se seteó
    if (!this.userId) {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          const u = JSON.parse(userStr);
          this.userId = String(u.id || u._id);
        } catch (e) {}
      }
    }
    return this.userId;
  }

  on(event, callback) {
    if (!this.listeners.has(event)) this.listeners.set(event, []);
    this.listeners.get(event).push(callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      const updated = this.listeners.get(event).filter((cb) => cb !== callback);
      this.listeners.set(event, updated);
    }
  }

  notifyListeners(event, data) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).forEach((cb) => {
        try { cb(data); } catch (e) {
          console.error(`Error en listener de ${event}:`, e);
        }
      });
    }
  }

  emit(event, data) {
    if (this.socket?.connected) {
      this.socket.emit(event, data);
    } else {
      console.warn(`⚠️ Socket no conectado, no se emitió: ${event}`);
    }
  }

  // ============================================
  // CHAT
  // ============================================
  joinChatRoom(roomId) {
    if (!roomId) return;
    if (this.currentRoom && this.currentRoom !== roomId) {
      this.emit('leave_room', this.currentRoom);
      this.emit('leave_chat', this.currentRoom);
    }
    this.currentRoom = roomId;
    // Emitir ambos para compatibilidad con backend nuevo y legacy
    this.emit('join_room', roomId);
    this.emit('join_chat', roomId);
    console.log('📢 Unido a sala:', roomId);
  }

  leaveChatRoom(roomId) {
    if (!roomId) return;
    this.emit('leave_room', roomId);
    this.emit('leave_chat', roomId);
    if (this.currentRoom === roomId) this.currentRoom = null;
    console.log('📢 Salido de sala:', roomId);
  }

  sendMessage(roomId, text, type = 'text', attachments = []) {
    this.emit('send_message', { roomId, text, type, attachments });
  }

  markMessageRead(messageId, roomId) {
    this.emit('mark_read', { messageId, roomId });
  }

  markRoomRead(roomId) {
    this.emit('mark_room_read', { roomId });
  }

  sendTyping(roomId, isTyping) {
    this.emit('typing', { roomId, isTyping });
  }

  requestLiveStats() {
    this.emit('request-stats');
  }

  isConnected() {
    return this.socket && this.socket.connected;
  }
}

export default new SocketService();
